/* eslint-disable sonarjs/no-duplicate-string */
import { useDisclosure } from '@mantine/hooks';
import {
  Button, Flex, Text
} from '@huspy/briks-web';
import { useTranslation } from 'react-i18next';
import { BaseSlido } from '@modules/core/components/BaseSlido';
import {
  Plus, UserPlus, UserSquare
} from '@huspy/briks-icons';
import {
  CaseFormInput,
  CaseFormSelectInput,
} from '@modules/opportunities/presentation/pages/CaseCreation/components/CaseInputs';
import { scrollToError } from '@shared/utils';
import { Tabs } from '@mantine/core';
import briksTheme from '@shared/briks-theme';
import { ExistingClientSelect } from '@modules/clients/presentation/v2/features/ExistingClientSelect';
import { useLocation } from '@tanstack/react-router';
import { slidoDeepLink } from '@modules/core/utils';
import { useEffect, useState } from 'react';
import { useCreateProposalState } from './useCreateProposalState';
import { ProposalCreationSlidoProps, ExistingClient } from '../types';

export const ProposalCreationSlidoEs = ({
  hideTabs = false,
  tab = 'newClient',
  existingClientData,
}: ProposalCreationSlidoProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<string | null>(tab);
  const {
    form,
    offerOptions,
    citizenStatusOptions,
    employmentStatusOptions,
    onSubmit,
    resetForm,
  } = useCreateProposalState();
  const { hash } = useLocation();
  const [opened, { open, close }] = useDisclosure(hash === slidoDeepLink.PROPOSAL_CREATION);

  const handleOnClose = () => {
    resetForm();
    close();
  };

  useEffect(() => {
    if (!hideTabs) {
      form.setValues({
        ...form.values,
        first_name: '',
        last_name: '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  return (
    <>
      <BaseSlido
        opened={ opened }
        close={ handleOnClose }
        title={ (
          <Flex direction='column' gap='2' p='6' pb='0'>
            <Text size='5xl'>
              {t('proposals.proposalsCreate.title')}
            </Text>
          </Flex>
        ) }
        hasDivider
        footer={ (
          <Flex justify='end' gap='2'>
            <Button
              type='button'
              onClick={ handleOnClose }
              variant='secondary'
              data-test='discard-btn'
            >
              {t('clients.formSections.discardButton')}
            </Button>
            <Button
              type='submit'
              data-test='save-btn'
              onClick={ onSubmit }
            >
              {t('proposals.proposalsCreate.title')}
            </Button>
          </Flex>
        ) }
      >
        <Flex h='100%' w='100%'>
          <Tabs defaultValue='newClient' onChange={ setActiveTab } w='100%'>
            <Tabs.List display='display'>
              <Tabs.Tab
                value='newClient'
                leftSection={ (
                  <Flex align='center' justify='center' h='100%' w='100%' mr='1'>
                    <UserPlus color={ briksTheme.colors['neutral.600'] } />
                  </Flex>
                ) }
                py='8px'
                pl='24px'
              >
                <Text size='md' mr='4' color='neutral.600' whiteSpace='nowrap'>
                  {t('proposals.proposalsCreate.title')}
                </Text>
              </Tabs.Tab>
              <Tabs.Tab
                value='existingClient'
                leftSection={ (
                  <Flex align='center' justify='center' h='100%' w='100%' mr='1'>
                    <UserSquare color={ briksTheme.colors['neutral.600'] } />
                  </Flex>
                ) }
              >
                <Text size='md' mr='4' color='neutral.600' whiteSpace='nowrap'>
                  {t('clients.formSections.existingClientTab')}
                </Text>
              </Tabs.Tab>
            </Tabs.List>
            <form onSubmit={ form.onSubmit(onSubmit, scrollToError) }>
              <Flex direction='column' gap='4' p='6'>
                <Tabs.Panel value='newClient'>
                  <Flex gap='4'>
                    <CaseFormInput
                      name='first_name'
                      label='firstName'
                      data-test='input-firstname'
                      form={ form }
                    />
                    <CaseFormInput
                      name='last_name'
                      label='lastName'
                      data-test='input-lastname'
                      form={ form }
                    />
                  </Flex>
                </Tabs.Panel>
                <Tabs.Panel value='existingClient'>
                  <ExistingClientSelect
                    label={ t('clients.formSections.selectClient') }
                    placeholder={ t('clients.formSections.selectClient') }
                    onValueChange={ (clientData: ExistingClient) => {
                      form.setValues({
                        ...form.values,
                        first_name: clientData!.firstName,
                        last_name: clientData!.lastName,
                      });
                    } }
                    defaultValue={ existingClientData }
                    disabled={ !!existingClientData }
                    isError={ !!form.errors?.first_name }
                  />
                </Tabs.Panel>
                <Flex gap='4'>
                  <CaseFormSelectInput
                    name='residency_status'
                    data-test='select-residency-status'
                    label='residencyStatus'
                    form={ form }
                    data={ citizenStatusOptions }
                  />
                  <CaseFormSelectInput
                    name='employment_status'
                    data-test='select-employment-status'
                    label='employmentStatus'
                    form={ form }
                    data={ employmentStatusOptions }
                  />
                </Flex>
                <Flex gap='4'>
                  <CaseFormSelectInput
                    w='100%'
                    name='offers_count'
                    data-test='select-offers-count'
                    label='offersCount'
                    data={ offerOptions }
                    form={ form }
                  />
                </Flex>
              </Flex>
            </form>
          </Tabs>
        </Flex>
      </BaseSlido>
      <Button onClick={ open } data-test='create-proposal-btn'>
        <Plus width={ 24 } color='inherit' />
        {t('proposals.createProposal')}
      </Button>
    </>
  );
};
